import { useRef, useState } from 'react'
import { PiPaperclip } from 'react-icons/pi'
import { FilterChip } from '@firstbite/ui'

/** Existing note attachment (server record). */
export interface NoteAttachment {
  id: number
  original_filename: string
}

const chipLabel = (name: string) => (
  <span className="inline-flex items-center gap-1">
    <PiPaperclip size={12} />
    <span className="max-w-[10rem] truncate">{name}</span>
  </span>
)

/**
 * Attachment add/remove for a note EDIT surface on the entity detail pages —
 * the piece the fb-ui RichTextEditor (a bare contentEditable) doesn't bundle.
 * Owns the "mark existing for deletion" + "stage new files" state and renders
 * removable chips; the caller passes `toDelete` / `filesToAdd` to
 * editDealNote / editContactNote. Wire `triggerFilePicker` to the toolbar's
 * `attachment` command. Shared so both detail pages behave identically.
 */
export function useNoteEditAttachments(existing: NoteAttachment[]) {
  const [toDelete, setToDelete] = useState<number[]>([])
  const [filesToAdd, setFilesToAdd] = useState<File[]>([])
  const fileInputRef = useRef<HTMLInputElement>(null)

  const triggerFilePicker = () => fileInputRef.current?.click()
  const reset = () => {
    setToDelete([])
    setFilesToAdd([])
  }

  const remaining = existing.filter((a) => !toDelete.includes(a.id))

  const node = (
    <>
      <input
        ref={fileInputRef}
        type="file"
        multiple
        hidden
        aria-label="Attach files to note"
        onChange={(e) => {
          const files = Array.from(e.target.files ?? [])
          if (files.length) setFilesToAdd((prev) => [...prev, ...files])
          e.target.value = ''
        }}
      />
      {remaining.length > 0 || filesToAdd.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {remaining.map((a) => (
            <FilterChip
              key={a.id}
              value={chipLabel(a.original_filename)}
              removeLabel={`Remove ${a.original_filename}`}
              onRemove={() => setToDelete((prev) => [...prev, a.id])}
            />
          ))}
          {filesToAdd.map((f, i) => (
            <FilterChip
              key={`${f.name}-${i}`}
              value={chipLabel(f.name)}
              removeLabel={`Remove ${f.name}`}
              onRemove={() =>
                setFilesToAdd((prev) => prev.filter((_, idx) => idx !== i))
              }
            />
          ))}
        </div>
      ) : null}
    </>
  )

  return { toDelete, filesToAdd, node, triggerFilePicker, reset }
}
